import { createAction, createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './types';
import { UserInfo } from '../interface/users';
import { UserAPI } from '../apis/users/index';
import { CurrencyType } from '../interface/bets';
import axios from 'axios';
import { getUserFavorites } from './favoriteSlice';
import { fetchSettings } from './settingSlice';

const initialState = {} as UserInfo;

export const authenticate = createAsyncThunk(
  'user/authenticate',
  async (
    payload: {
      MerchantCode: string;
      ProviderCode: string;
      MerchantToken: string;
      UserName: string;
      Currency: string;
    },
    { dispatch }
  ) => {
    const { MerchantCode, ProviderCode, MerchantToken, UserName, Currency } = payload;

    if (process.env.NODE_ENV === 'development') {
      // check there is a 'UserInfo' and 'ExpireTime' in localStorage
      const UserInfo = localStorage.getItem('UserInfo');
      const ExpireTime = localStorage.getItem('ExpireTime');
      if (UserInfo && ExpireTime) {
        // check the ExpireTime is not expired
        if (new Date(ExpireTime) > new Date()) {
          // if not expired, return the UserInfo
          dispatch(getUserFavorites({ token: JSON.parse(UserInfo).SsToken }));
          return {
            ...JSON.parse(UserInfo),
            Settings: await dispatch(fetchSettings({ token: JSON.parse(UserInfo).SsToken })),
            ...{ Currency: CurrencyType[Currency as keyof typeof CurrencyType], MerchantCode: MerchantCode },
          };
        }
      }
    }

    try {
      const response = await UserAPI.authenticate(MerchantCode, ProviderCode, MerchantToken, UserName, Currency);

      const Settings = await dispatch(fetchSettings({ token: response.SsToken })).unwrap();

      let CurrencyLabel = 'VND';

      if (Currency in CurrencyType) {
        CurrencyLabel = CurrencyType[Currency as keyof typeof CurrencyType];
      }

      if (process.env.NODE_ENV === 'development') {
        localStorage.setItem('UserInfo', JSON.stringify({ ...response, Currency: payload.Currency }));
        localStorage.setItem('ExpireTime', new Date(new Date().getTime() + 60 * 1000).toString());
      }

      dispatch(
        getUserFavorites({
          token: response.SsToken,
        })
      );

      return { ...response, Settings, ...{ Currency: CurrencyLabel } };
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw new Error(e.response?.data.Message ?? 'unexpected');
      }
    }
  }
);

export const userPolling = createAsyncThunk('user/polling', async (_, { getState }) => {
  try {
    const { SsToken: token, Language } = (getState() as RootState).user;
    return await UserAPI.getUserPolling(token, Language);
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new Error(e.response?.data.Message ?? 'unexpected');
    }
  }
});
export const userPollingStarted = createAction('user/pollingStarted');
export const userPollingStopped = createAction('user/pollingStopped');

export const userSlice = createSlice({
  name: 'member',
  initialState,
  reducers: {
    setMember: (state: UserInfo, action: PayloadAction<Partial<UserInfo>>) => {
      let currencyLabel = 'VND';

      if (state.Currency in CurrencyType) {
        currencyLabel = CurrencyType[state.Currency as keyof typeof CurrencyType];
      }

      Object.assign(state, action.payload, { Currency: currencyLabel });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authenticate.fulfilled, (state, action) => {
      Object.assign(state, action.payload);
    });
  },
});

export const { setMember } = userSlice.actions;

export const selectMember = (state: RootState) => state.user;

export default userSlice.reducer;

export const getBetLimitBySportTypeSelector = createSelector(
  (state: RootState) => state.user.Settings,
  (_: RootState, { sportType }: { sportType: string }) => sportType,
  (settings, sportType) => {
    return (
      settings?.BetLimits?.find((item: any) => item.SportType === sportType) ||
      settings?.BetLimits?.find((item: any) => item.SportType === 'OT')
    );
  }
);

export const selectIsLoggedIn = createSelector(
  (state: RootState) => state.user,
  (user) => Boolean(user.SsToken)
);
